import React, { useState } from 'react';
import { X, Search, Rss, Scroll, FileSpreadsheet, FileText, Presentation, Instagram, Facebook, Youtube, LayoutTemplate, Trophy, Type, GripHorizontal } from 'lucide-react';
import alJazeeraPaths from "../../../imports/svg-75v5eytj43";
import wehjaClockPaths from "../../../imports/svg-ugkcee0qsx";
import clockPaths from "../../../imports/svg-3xg2jb8v3u";
import dynamicClockPaths from "../../../imports/svg-zdurhmesox";
import weatherPaths from "../../../imports/svg-z7yxkuxy65";

import clsx from "clsx";

import { useGetMyWidgetsQuery, Widget } from '../../features/widgets/widgetsApi';

interface AppsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddApp?: (appName: string, widget?: Widget) => void;
  onOpenCreateAppModal?: () => void;
  apps?: typeof APPS_DATA;
}

// Draggable App Wrapper
function DraggableApp({ name, children, onClick, className }: { name: string, children: React.ReactNode, onClick: () => void, className?: string }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('element-type', 'app');
        e.dataTransfer.setData('app-name', name);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className={clsx(className, 'opacity-100')}
      onClick={onClick}
    >
      {children}
    </div>
  );
}





// Helpers from Figma import
function Wrapper1({ children }: React.PropsWithChildren) {
  return (
    <div className="h-[40px] relative rounded-[6px] shrink-0 w-[48px]">
      <div className="overflow-clip relative rounded-[inherit] size-full flex items-center justify-center bg-gray-50">{children}</div>
      <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[6px]" />
    </div>
  );
}

// Mock data for "Apps" tab (Widgets)
export const APPS_DATA = [
  {
    id: 'clock',
    displayName: 'Clock',
    category: 'Clock',
    icon: (
      <div className="relative w-8 h-8">
        <div className="absolute inset-[-31.58%_-31.58%_-36.84%_-36.84%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 53.8947 53.8947">
            <path clipRule="evenodd" d={clockPaths.p15da980} fill="var(--fill-0, #42A5F5)" fillRule="evenodd" />
          </svg>
        </div>

        <div className="absolute inset-[-31.58%_-31.58%_-36.84%_-36.84%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 32">
            <g></g>
          </svg>
        </div>
        <div className="absolute inset-[6.58%_6.58%_1.32%_1.32%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 29.4737 29.4737">
            <path clipRule="evenodd" d={clockPaths.pf645a00} fill="var(--fill-0, #3677B8)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[13.3%_13.29%_8.02%_8.03%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 25.1755 25.1755">
            <path clipRule="evenodd" d={clockPaths.p319950f0} fill="var(--fill-0, #A0DBE0)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[15.21%_15.21%_9.95%_9.95%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 23.9474 23.9474">
            <path clipRule="evenodd" d={clockPaths.p309b0b80} fill="var(--fill-0, #B2EBF2)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.08%_52.06%_47.36%_47.37%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.181316 0.181841">
            <path clipRule="evenodd" d={clockPaths.p31124000} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.63%_52.63%_46.75%_46.75%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.197895 0.197368">
            <path clipRule="evenodd" d={clockPaths.pb497a00} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.63%_52.06%_46.8%_47.37%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.181843 0.181316">
            <path clipRule="evenodd" d={clockPaths.p2ca91200} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.01%_52.63%_47.37%_46.75%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.197368 0.197895">
            <path clipRule="evenodd" d={clockPaths.p1e607b00} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.63%_52.63%_46.8%_46.8%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.181315 0.181843">
            <path clipRule="evenodd" d={clockPaths.p152ff400} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.08%_52.63%_47.36%_46.8%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.181841 0.181315">
            <path clipRule="evenodd" d={clockPaths.p74c9a00} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.01%_52.01%_47.37%_47.37%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.197895 0.197368">
            <path clipRule="evenodd" d={clockPaths.p1038d300} fill="var(--fill-0, #424242)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[27.41%_47.96%_34%_34%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 5.77164 12.3514">
            <path clipRule="evenodd" d={clockPaths.p411c300} fill="var(--fill-0, #263238)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[52.63%_52.01%_46.75%_47.37%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 32">
            <g></g>
          </svg>
        </div>
        <div className="absolute inset-[52.63%_52.63%_47.37%_47.37%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 32">
            <g></g>
          </svg>
        </div>
        <div className="absolute inset-[72.57%_72.56%_21.64%_21.66%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1.85001 1.85001">
            <path clipRule="evenodd" d={clockPaths.p2989a670} fill="var(--fill-0, #0097A7)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[72.57%_26.92%_21.64%_67.3%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1.85001 1.85001">
            <path clipRule="evenodd" d={clockPaths.p2c2b2800} fill="var(--fill-0, #0097A7)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[26.91%_72.56%_67.31%_21.66%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1.85001 1.85001">
            <path clipRule="evenodd" d={clockPaths.p23413780} fill="var(--fill-0, #0097A7)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[51.69%_16.65%_46.45%_75.94%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2.36876 0.598074">
            <path clipRule="evenodd" d={clockPaths.p234e0e80} fill="var(--fill-0, #006064)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[51.69%_81.21%_46.45%_11.39%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2.36877 0.598074">
            <path clipRule="evenodd" d={clockPaths.p3f1c5b80} fill="var(--fill-0, #006064)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[81.21%_51.7%_11.39%_46.43%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.598074 2.36876">
            <path clipRule="evenodd" d={clockPaths.p8762600} fill="var(--fill-0, #006064)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[16.65%_51.7%_75.94%_46.43%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.598074 2.36877">
            <path clipRule="evenodd" d={clockPaths.p1c120480} fill="var(--fill-0, #006064)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[26.91%_26.92%_67.31%_67.3%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1.85001 1.85001">
            <path clipRule="evenodd" d={clockPaths.p2ef40800} fill="var(--fill-0, #0097A7)" fillRule="evenodd" />
          </svg>
        </div>
        <div className="absolute inset-[50.43%_50.35%_45.1%_45.18%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1.42913 1.42913">
            <path d={clockPaths.p2e4bfb00} fill="var(--fill-0, #37474F)" />
          </svg>
        </div>
        <div className="absolute inset-[51.81%_51.73%_46.47%_46.55%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 0.549641 0.549641">
            <path d={clockPaths.pbf9ee80} fill="var(--fill-0, #B0BEC5)" />
          </svg>
        </div>
      </div>
    ),
    color: 'bg-transparent'
  },
  {
    id: 'weather',
    displayName: 'Weather',
    category: 'Utility',
    icon: (
      <div className="w-8 h-8">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 32">
          <g id="Apps">
            <path d={weatherPaths.p3f51de00} fill="white" id="Vector" />
          </g>
        </svg>
      </div>
    ),
    color: 'bg-[#009688]'
  },
  {
    id: 'rss',
    displayName: 'RSS Feed',
    category: 'Feed',
    icon: <Rss className="w-8 h-8 text-white" />,
    color: 'bg-orange-500'
  },
  {
    id: 'text',
    displayName: 'Text',
    category: 'Core',
    icon: <Type className="w-8 h-8 text-white" />,
    color: 'bg-indigo-600'
  },
  {
    id: 'scroller',
    displayName: 'Scroller',
    category: 'Core',
    icon: (
      <div className="w-8 h-8 flex items-center justify-center">
        <Scroll className="w-7 h-7 text-white" />
      </div>
    ),
    color: 'bg-[#002B5B]'
  },
  {
    id: 'canva',
    displayName: 'Canva',
    category: 'Creative',
    icon: <LayoutTemplate className="w-8 h-8 text-white" />,
    color: 'bg-cyan-500'
  },
  {
    id: 'gsheet',
    displayName: 'G Sheet',
    category: 'Workspace',
    icon: <FileSpreadsheet className="w-8 h-8 text-white" />,
    color: 'bg-green-600'
  },
  {
    id: 'gslides',
    displayName: 'G Slides',
    category: 'Workspace',
    icon: <Presentation className="w-8 h-8 text-white" />,
    color: 'bg-yellow-500'
  },
  {
    id: 'gdocs',
    displayName: 'G Docs',
    category: 'Workspace',
    icon: <FileText className="w-8 h-8 text-white" />,
    color: 'bg-blue-600'
  },
  {
    id: 'cnn',
    displayName: 'CNN News',
    category: 'News',
    icon: <div className="font-bold text-white text-xs">CNN</div>,
    color: 'bg-red-600'
  },
  {
    id: 'saudi',
    displayName: 'Saudi Gaz...',
    category: 'News',
    icon: <div className="font-bold text-white text-[10px] leading-tight text-center">Saudi<br />Gazette</div>,
    color: 'bg-[#C6B995]'
  },
  {
    id: 'toi',
    displayName: 'TOI News',
    category: 'News',
    icon: <div className="font-bold text-white text-xs">TOI</div>,
    color: 'bg-red-700'
  },
  {
    id: 'live',
    displayName: 'Live Stream',
    category: 'Media',
    icon: <Youtube className="w-8 h-8 text-white" />,
    color: 'bg-red-500'
  },
  {
    id: 'aljazeera',
    displayName: 'Al Jazeera',
    category: 'News',
    icon: (
      <div className="relative size-full flex items-center justify-center p-2" data-name="Al_Jazeera 1">
        <div className="relative w-full h-full" data-name="g15972">
          <svg className="block w-full h-full" fill="none" preserveAspectRatio="xMidYMid meet" viewBox="0 0 19.0049 30.3104">
            <g id="g15972">
              <path d={alJazeeraPaths.p3a870a80} fill="var(--fill-0, #D4A10F)" id="path3297" />
              <path d={alJazeeraPaths.p8672300} fill="var(--fill-0, #D4A10F)" id="path3295" />
              <path d={alJazeeraPaths.p1bdc4e00} fill="var(--fill-0, #D4A10F)" id="path3291" />
              <path d={alJazeeraPaths.p2da13e80} fill="var(--fill-0, #D4A10F)" id="path3285" />
              <path d={alJazeeraPaths.p3467b000} fill="var(--fill-0, #D4A10F)" id="path3279" />
              <path d={alJazeeraPaths.p24b08600} fill="var(--fill-0, #D4A10F)" id="path3277" />
              <path d={alJazeeraPaths.p2d2b9680} fill="var(--fill-0, #D4A10F)" id="path3326" />
              <path d={alJazeeraPaths.p22799970} fill="var(--fill-0, #D4A10F)" id="rect3340" />
              <path d={alJazeeraPaths.p2ee26380} fill="var(--fill-0, #D4A10F)" id="rect3344" />
              <path d={alJazeeraPaths.p14031400} fill="var(--fill-0, #D4A10F)" id="rect3346" />
              <path d={alJazeeraPaths.p371b9b00} fill="var(--fill-0, #D4A10F)" id="rect3348" />
            </g>
          </svg>
        </div>
      </div>
    ),
    color: 'bg-white border border-gray-100'
  },
  {
    id: 'bbc',
    displayName: 'BBC News',
    category: 'News',
    icon: <div className="font-bold text-white text-xs">BBC</div>,
    color: 'bg-red-800'
  },
  {
    id: 'wejhaclock',
    displayName: 'Wejha Clo..',
    category: 'Clock',
    icon: (
      <div className="relative w-full h-full">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8">
          <div className="absolute left-0 top-0 w-8 h-8">
            <svg className="block size-full" fill="none" viewBox="0 0 32 32">
              <g clipPath="url(#clip0_wehja_globe)">
                <path d={wehjaClockPaths.p5cd1b00} fill="#64B5F6" />
              </g>
              <defs>
                <clipPath id="clip0_wehja_globe">
                  <rect fill="white" width="32" height="32" />
                </clipPath>
              </defs>
            </svg>
          </div>

          <div className="absolute left-[13.33px] top-[13.33px] w-[18.667px] h-[18.667px]">
            <svg className="block size-full" fill="none" viewBox="0 0 18.6667 18.668">
              <path d={wehjaClockPaths.p4ad2480} fill="#E0E0E0" />
              <path clipRule="evenodd" d={wehjaClockPaths.pe45d000} fill="#6D4C41" fillRule="evenodd" />
              <path clipRule="evenodd" d={wehjaClockPaths.p1a7f5800} fill="#CECECE" fillRule="evenodd" />
              <path clipRule="evenodd" d={wehjaClockPaths.p34595000} fill="#424242" fillRule="evenodd" />
              <path d={wehjaClockPaths.p3c51ee00} fill="#9E9E9E" />
              <path d={wehjaClockPaths.p20ac5000} fill="#616161" />
              <path d={wehjaClockPaths.p26c48b80} fill="#424242" />
              <path d={wehjaClockPaths.p2fa3b700} fill="#9E9E9E" />
              <path d={wehjaClockPaths.p33a04770} fill="#424242" />
              <path d={wehjaClockPaths.p2359ff00} fill="#9E9E9E" />
            </svg>
          </div>
        </div>
      </div>
    ),
    color: 'bg-[#e8f3fe]'
  },
  {
    id: 'dynamic',
    displayName: 'Dynamic...',
    category: 'Clock',
    icon: (
      <div className="relative w-8 h-8">
        <div className="absolute inset-[12.5%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
            <g>
              <path d={dynamicClockPaths.p1f1b6800} fill="var(--fill-0, #B2EBF2)" id="Vector" />
              <path clipRule="evenodd" d={dynamicClockPaths.p3aa64e80} fill="var(--fill-0, #3677B8)" fillRule="evenodd" id="Vector_2" />
              <path clipRule="evenodd" d={dynamicClockPaths.p282798a0} fill="var(--fill-0, #263238)" fillRule="evenodd" id="Vector_3" />
              <path clipRule="evenodd" d={dynamicClockPaths.p35720f00} fill="var(--fill-0, #546E7A)" fillRule="evenodd" id="Vector_4" />
              <path d={dynamicClockPaths.pb5c6370} fill="var(--fill-0, #263238)" id="Vector_5" />
              <path d={dynamicClockPaths.p3a40be80} fill="var(--fill-0, #D50000)" id="Vector_6" />
              <path d={dynamicClockPaths.p7da3d00} fill="var(--fill-0, #D50000)" id="Vector_7" />
            </g>
          </svg>
        </div>
        <div className="absolute bottom-[12.5%] left-[12.5%] right-1/2 top-[12.5%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 12 24">
            <g>
              <path clipRule="evenodd" d={dynamicClockPaths.p3a498480} fill="var(--fill-0, #A0D7E0)" fillRule="evenodd" id="Vector" />
              <path clipRule="evenodd" d={dynamicClockPaths.p17773b00} fill="var(--fill-0, #3677B8)" fillRule="evenodd" id="Vector_2" />
              <path clipRule="evenodd" d={dynamicClockPaths.p29e75000} fill="var(--fill-0, #223539)" fillRule="evenodd" id="Vector_3" />
              <path clipRule="evenodd" d={dynamicClockPaths.p160fdb00} fill="var(--fill-0, #4C6B74)" fillRule="evenodd" id="Vector_4" />
              <path clipRule="evenodd" d={dynamicClockPaths.p2398db00} fill="var(--fill-0, #223539)" fillRule="evenodd" id="Vector_5" />
              <path d={dynamicClockPaths.p37d0fd00} fill="var(--fill-0, #C00806)" id="Vector_6" />
              <path clipRule="evenodd" d={dynamicClockPaths.p1fbf0e00} fill="var(--fill-0, #C00806)" fillRule="evenodd" id="Vector_7" />
            </g>
          </svg>
        </div>
      </div>
    ),
    color: 'bg-[#42a5f5]'
  },
  {
    id: 'instagram',
    displayName: 'Instagram',
    category: 'Social',
    icon: <Instagram className="w-8 h-8 text-white" />,
    color: 'bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500'
  },
  {
    id: 'facebook',
    displayName: 'Facebook',
    category: 'Social',
    icon: <Facebook className="w-8 h-8 text-white" />,
    color: 'bg-blue-600'
  }
];

export function AppsPanel({ isOpen, onClose, onAddApp, apps }: AppsPanelProps) {
  const [activeTab, setActiveTab] = useState<'apps' | 'cms_apps' | 'saved'>('cms_apps');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when tab or search changes
  React.useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch]);

  const { data: widgetsData, isLoading, isFetching } = useGetMyWidgetsQuery({
    page,
    limit: 20,
    search: debouncedSearch
  }, {
    skip: !isOpen || activeTab !== 'cms_apps'
  });

  const totalPages = widgetsData?.totalPages || 1;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (activeTab !== 'cms_apps' || isFetching || page >= totalPages) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Load more when 50px from the bottom
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setPage(prev => prev + 1);
    }
  };

  const displayWidgets = apps || APPS_DATA;

  const filteredWidgets = displayWidgets.filter(app =>
    app.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const savedWidgets = widgetsData?.results || [];

  const filteredCMSApps = savedWidgets; // API already handles search now

  return (
    <>


      {/* Panel */}
      <div
        className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between bg-white z-10">
          <h2 className="font-semibold text-gray-900 text-lg">Apps</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center px-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('cms_apps')}
            className={clsx(
              "pb-3 pt-1 text-sm font-medium mr-6 transition-all relative group",
              activeTab === 'cms_apps' ? "app-active-state" : "text-gray-500 hover:text-gray-700"
            )}
          >
            <div 
                className="app-tab-indicator" 
                style={{ '--background-width': activeTab === 'cms_apps' ? '100%' : '0%' } as React.CSSProperties} 
            />
            CMS Apps
          </button>
        </div>

        {/* Content */}
        <div 
          ref={containerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar"
        >

          {/* Search Bar */}
          <div className="content-stretch flex flex-col h-[40px] items-start relative shrink-0 w-full mb-4">
            <div className="h-[40px] relative rounded-[8px] shrink-0 w-full">
              <div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full bg-white border border-[#e4e7ec]">
                <div className="content-stretch flex items-center px-[12px] py-[3px] relative size-full">
                  <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                    <Search className="w-4 h-4 text-[#98A2B3]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={
                        activeTab === 'apps' ? "Search app..." :
                          activeTab === 'cms_apps' ? "Search CMS app..." :
                            "Search saved..."
                      }
                      className="font-sans text-[14px] text-[#101928] placeholder-[#98a2b3] bg-transparent outline-none flex-1 h-full text-left"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Switch */}
          {activeTab === 'cms_apps' ? (
            isLoading && !widgetsData ? (
              <div className="col-span-2 flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#30A84B]"></div>
              </div>
            ) : (
              <div className={clsx("grid grid-cols-2 gap-3 transition-opacity duration-200", isFetching && page === 1 && "opacity-50")}>
                {filteredCMSApps.map((app) => (
                  <DraggableApp
                    key={app._id}
                    name={app.name}
                    onClick={() => onAddApp?.(app.name, app)}
                    className="flex flex-col items-center gap-2 p-3 bg-gray-50/50 hover:bg-white rounded-xl border border-transparent hover:border-gray-200 hover:shadow-sm transition-all group cursor-pointer relative"
                  >
                    <div className="relative">
                      <Wrapper1>
                        {app.preview_image ? (
                          <img
                            src={app.preview_image}
                            alt={app.name}
                            className="w-full h-full object-cover rounded-[inherit] transition-transform group-hover:scale-110 duration-300"
                          />
                        ) : (
                          <div className="text-[#30A84B] transition-transform group-hover:scale-110 duration-300">
                            <Trophy className="w-5 h-5" />
                          </div>
                        )}
                      </Wrapper1>
                      <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-[#30A84B] w-2 h-2 rounded-full border border-white" />
                      </div>
                    </div>
                    <div className="flex flex-col items-center text-center gap-0.5 w-full">
                      <p className="font-sans font-bold text-[#101928] text-[13px] leading-tight truncate w-full">
                        {app.name}
                      </p>
                      <p className="font-sans text-[10px] text-[#667185] font-medium">
                        {new Date(app.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 cursor-grab active:cursor-grabbing p-1 hover:text-[#30A84B]" title="Drag to reorder">
                      <GripHorizontal className="w-4 h-4" />
                    </div>
                  </DraggableApp>
                ))}

                {isFetching && page > 1 && (
                  <div className="col-span-2 flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#30A84B]"></div>
                  </div>
                )}

                {filteredCMSApps.length === 0 && (
                  <div className="col-span-2 text-center py-12">
                    <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Search className="w-6 h-6 text-gray-300" />
                    </div>
                    <p className="text-gray-900 font-medium text-sm">No results</p>
                    <p className="text-gray-500 text-xs mt-1">No CMS apps found matching "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )
          ) : activeTab === 'apps' ? (
            <div className="grid grid-cols-2 gap-3">
              {filteredWidgets.map((app) => (
                <DraggableApp
                  key={app.id}
                  name={app.displayName}
                  onClick={() => onAddApp?.(app.displayName)}
                  className="flex flex-col items-center gap-2 p-4 bg-white hover:bg-gray-50 rounded-2xl border border-gray-100 hover:border-[#30A84B]/30 hover:shadow-lg hover:shadow-[#30A84B]/5 transition-all group cursor-pointer relative overflow-hidden"
                >
                  <div className={clsx(
                    "w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300 relative z-10",
                    app.color || 'bg-gray-100'
                  )}>
                    <div className="w-9 h-9 flex items-center justify-center">
                      {app.icon}
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center w-full relative z-10">
                    <p className="font-sans font-bold text-[#101928] text-[12px] leading-tight truncate w-full">
                      {app.displayName}
                    </p>
                  </div>

                  <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-[#30A84B]/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                </DraggableApp>
              ))}

              {filteredWidgets.length === 0 && (
                <div className="col-span-2 text-center py-12">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-gray-200">
                    <Search className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-gray-900 font-bold text-sm">No apps found</p>
                  <p className="text-gray-500 text-xs mt-1 px-8">Try another keyword.</p>
                </div>
              )}
            </div>
          ) : (
            /* Saved Tab Placeholder */
            <div className="col-span-2 text-center py-12">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-gray-200">
                <LayoutTemplate className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-900 font-bold text-sm">No saved apps</p>
              <p className="text-gray-500 text-xs mt-1 px-8 text-center leading-relaxed">
                Configure apps and save them to this project for quick access.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

